import { useEffect } from "react";
import { CheckCircle2, Circle } from "lucide-react";

// Shared building blocks for the cinematic preview sections below the hero.
export const SCENE = (name) => ({
  src: `/cinematic/scene-${name}.webp`,
  srcSet: `/cinematic/scene-${name}-760.webp 760w, /cinematic/scene-${name}.webp 1264w`,
});
const SHOT = (screen) => `/marketing/product/${screen}.webp`;
const SHOT_SET = (screen) => `/marketing/product/${screen}-900.webp 900w, /marketing/product/${screen}.webp 1440w`;

export function useReveal(ref) {
  useEffect(() => {
    const root = ref.current;
    if (!root || !window.IntersectionObserver || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    root.classList.add("fx-anim");
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
    }), { rootMargin: "0px 0px -10% 0px", threshold: 0.12 });
    root.querySelectorAll(".fx-reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ref]);
}

export function Laptop({ screen, caption, className = "" }) {
  return (
    <figure className={`fx-laptop ${className}`}>
      <div className="fx-laptop-screen">
        <div className="fx-laptop-bar" aria-hidden="true"><i /><i /><i /><span>Fieldlink · {caption}</span></div>
        <img src={SHOT(screen)} srcSet={SHOT_SET(screen)} sizes="(max-width: 760px) 92vw, 760px" width="1440" height="1000"
          loading="lazy" decoding="async" alt={`${caption} in the current Fieldlink interface, using demonstration records`} />
      </div>
      <div className="fx-laptop-base" aria-hidden="true" />
    </figure>
  );
}

export function Phone({ caption = "Mobile Job view", className = "" }) {
  return (
    <figure className={`fx-device-phone ${className}`}>
      <span className="fx-device-notch" aria-hidden="true" />
      <img src={SHOT("mobile")} width="390" height="844" loading="lazy" decoding="async"
        alt={`${caption} in the Fieldlink mobile app, using demonstration records`} />
    </figure>
  );
}

export function Glass({ title, meta, className = "", children, testid }) {
  return (
    <div className={`fx-glass ${className}`} data-testid={testid}>
      <div className="fx-glass-head"><span>{title}</span>{meta && <small>{meta}</small>}</div>
      {children}
    </div>
  );
}

export const Rows = ({ rows }) => rows.map(([a, b, tone]) => (
  <div className="fx-glass-row" key={a}><span>{a}</span><b className={tone ? `is-${tone}` : ""}>{b}</b></div>
));

export const Checks = ({ items }) => items.map(([label, done]) => (
  <div className="fx-glass-check" key={label}>
    {done ? <CheckCircle2 size={15} className="is-ok" aria-hidden="true" /> : <Circle size={15} aria-hidden="true" />}<span>{label}</span>
  </div>
));

export const Meter = ({ label, value, pct }) => (
  <div className="fx-glass-meter">
    <div><span>{label}</span><b>{value}</b></div>
    <i><em style={{ width: `${pct}%` }} /></i>
  </div>
);
